import type { CardData, RoomSettings } from './types';

export const MIN_PLAYERS = 3;
export const MAX_PLAYERS = 16;
export const NAME_MAX = 20;
export const CARD_TEXT_MAX = 160;
export const WRITE_IN_MAX = 80;
export const PACK_CARDS_MAX = 600;

export const AVATARS = [
  '🪔', '🥭', '🍵', '🪁', '🐘', '🦚', '🥥', '🌶️', '🛺', '🏏', '🍛', '🧿',
  '🪷', '🐅', '🥘', '🎺', '🌙', '🕌', '🛕', '🥁', '🍋', '🐒', '🫖', '🎬',
];

export const SETTING_LIMITS = {
  handSize: { min: 5, max: 10 },
  pointsToWin: [0, 5, 7, 10, 15],
  roundTimer: [0, 60, 90, 120],
  writeIns: { min: 0, max: 5 },
  redraws: { min: 0, max: 3 },
  writeOwnEvery: [0, 3, 5],
  worstEvery: [0, 4, 6],
  teams: { min: 2, max: 4, nameMax: 16 },
} as const;

export const DEFAULT_SETTINGS: Omit<RoomSettings, 'packIds'> = {
  handSize: 7,
  pointsToWin: 7,
  roundTimer: 0,
  judgeMode: 'judge',
  writeIns: 2,
  redraws: 1,
  promptChoice: false,
  guessWho: false,
  teams: [],
  writeOwnEvery: 0,
  worstEvery: 0,
};

/** Suggested team names when the host turns teams on. */
export const TEAM_PRESETS: string[][] = [
  ['Kids', 'Parents'],
  ['Ladkewale', 'Ladkiwale'],
  ['Team Chai', 'Team Coffee'],
];
export const DEFAULT_TEAM_NAMES = ['Kids', 'Parents', 'Cousins', 'Uncles'];

export const BLANK = '___';
const BLANK_RE = /_{3}/g;

export function countBlanks(text: string): number {
  return (text.match(BLANK_RE) ?? []).length;
}

/**
 * Valid prompt shapes: no blank (question, pick 1), one blank, several blanks that all repeat
 * the same card (pick 1), or one card per blank (pick = blanks).
 */
export function isValidPick(text: string, pick: number): boolean {
  const blanks = countBlanks(text);
  if (pick === 1) return true;
  return pick >= 2 && pick <= 3 && blanks === pick;
}

/** Guess `pick` for a prompt a player typed: one card per blank, capped at 3. */
export function inferPick(text: string): number {
  const blanks = countBlanks(text);
  return blanks >= 2 && blanks <= 3 ? blanks : 1;
}

export type PromptPart = { kind: 'text'; value: string } | { kind: 'fill'; value: string; index: number } | { kind: 'blank'; index: number };

/**
 * Split a prompt into text runs and blanks, filling blanks with answers when given. A pick-1
 * prompt with several blanks repeats the one answer in every blank.
 */
export function splitPrompt(prompt: Pick<CardData, 'text' | 'pick'>, answers?: string[]): PromptPart[] {
  const parts: PromptPart[] = [];
  const pieces = prompt.text.split(BLANK_RE);
  pieces.forEach((piece, i) => {
    if (piece) parts.push({ kind: 'text', value: piece });
    if (i < pieces.length - 1) {
      const answerIndex = prompt.pick === 1 ? 0 : i;
      const answer = answers?.[answerIndex];
      parts.push(answer ? { kind: 'fill', value: answer, index: answerIndex } : { kind: 'blank', index: answerIndex });
    }
  });
  return parts;
}

/** Answers carry no trailing period ("Mummy's chappal." reads badly mid-sentence), but keep "a.m." and "...". */
export function stripTrailingPeriod(text: string): string {
  if (/\.\.$/.test(text) || /\b[a-z]\.[a-z]\.$/i.test(text)) return text;
  return text.replace(/\.$/, '');
}

export const WRITTEN_PROMPT_MIN = 8;

/**
 * Check a prompt a player wrote mid-game: it needs a blank (___) or has to be a question, and at
 * most three blanks. Returns the cleaned text and how many cards it asks for.
 */
export function checkWrittenPrompt(raw: string): { ok: true; text: string; pick: number } | { ok: false; error: string } {
  const text = cleanText(String(raw ?? ''));
  if (text.length < WRITTEN_PROMPT_MIN) return { ok: false, error: 'Write a little more than that.' };
  if (text.length > CARD_TEXT_MAX) return { ok: false, error: `Keep it under ${CARD_TEXT_MAX} characters.` };
  const blanks = countBlanks(text);
  if (blanks === 0 && !text.endsWith('?')) return { ok: false, error: 'Add a blank (___) or make it a question.' };
  if (blanks > 3) return { ok: false, error: 'Three blanks at most, please.' };
  const pick = inferPick(text);
  if (!isValidPick(text, pick)) return { ok: false, error: 'That prompt shape does not work. Try one blank.' };
  return { ok: true, text, pick };
}

/** Whether round `number` (1-based) is one of every `every` rounds. */
export function isEveryNth(every: number | undefined, number: number): boolean {
  return Boolean(every && every > 0 && number > 0 && number % every === 0);
}

export function normalizeRoomCode(code: string): string {
  return code.replace(/[^a-z]/gi, '').toUpperCase().slice(0, 4);
}

export function cleanText(text: string): string {
  return text
    .replace(/[‘’]/g, "'")
    .replace(/[“”]/g, '"')
    .replace(/…/g, '...')
    .replace(/_{3,}/g, BLANK)
    .replace(/\s+/g, ' ')
    .trim();
}

/** Tags used by the mini-games and filters. Difficulty is stored as a tag too. */
export const CARD_TAGS = [
  'film', 'song', 'tv', 'person', 'food', 'drink', 'place', 'festival', 'custom', 'family', 'brand',
  'cricket', 'sport', 'history', 'object', 'animal', 'clothing', 'phrase', 'internet', 'school',
  'wedding', 'work', 'money', 'body', 'emotion', 'politics', 'religion',
] as const;
export const DIFFICULTY_TAGS = ['easy', 'medium', 'hard'] as const;
